import { Injectable } from '@angular/core';
import { quickWebLogger } from '@quickweb/logger';
import { JsonConverterUtils } from 'src/app/data/CXOne/util/JsonConverterUtils';
import { extractError } from 'src/app/utils/error-utils';
import { getUserIfoInLocalStorage } from 'src/app/utils/get-user-info-In-localstorage';
import { MediaType } from 'src/app/utils/media-type';

type OptionalProps = Record<string, unknown>;
type LogTypes = keyof typeof quickWebLogger.logger;

export interface DatadogProps {
  attachData?: string | OptionalProps | unknown;
  mediaType: MediaType | null;
  masterId: string;
}

export interface CreateSessionSuccessProps extends DatadogProps {
  sessionId: string;
  interactionId: string;
}

export interface InteractionProps extends DatadogProps {
  interactionId: string;
}

export interface TemplateProps extends InteractionProps {
  template: OptionalProps;
}

export interface DatadogError extends DatadogProps {
  error: unknown;
}

type CloseTicketByEventType =
  | 'fecharSessao'
  | 'fecharSessao_uraTransferencia'
  | 'fecharSessao_uraAut'
  | 'fecharSessao_controleSessao'
  | string;

export interface CloseTicketByEventProps extends DatadogProps {
  sessionId?: string;
  interactionId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CIFDatadogService {
  readonly openTicket = {
    initialData: this.ticketInitialData.bind(this),
    invalidJsonParser: this.invalidJsonParser.bind(this),

    request: {
      create: this.createTicketRequest.bind(this),
      success: this.createTicketSuccess.bind(this),
      fail: this.createTicketFail.bind(this),
      duplicated: this.duplicatedTicketCreation.bind(this),
    },

    retrieve: {
      start: this.startRetrieveTicket.bind(this),
      success: this.successRetrieveTicket.bind(this),
      excessiveAttemptsError:
        this.excessiveAttemptsRetrieveTicketError.bind(this),
      fail: this.failRetrieveTicket.bind(this),
    },

    session: {
      request: this.createTicketSession.bind(this), // mostro o ticket em tela
      success: this.successCreateTicketSession.bind(this),
      fail: this.failCreateTicket.bind(this),
    },
  };

  readonly ura = {
    leaveToUra: this.uraLeaveToUra.bind(this),
    returnToUra: this.uraReturnToUra.bind(this),
    cancel: this.uraCancel.bind(this),
    fail: this.uraFail.bind(this),
  };

  readonly closeTicket = {
    start: this.beginCloseTicket.bind(this),
    success: this.successCloseTicket.bind(this),
    fail: this.failToCloseTicket.bind(this),
    relationshipFail: this.failToCloseTicketByRelationship.bind(this),
  };

  readonly updateTicket = {
    start: this.beginUpdateTicket.bind(this),
    notFound: this.failToUpdateNotFoundTicket.bind(this),
    success: this.successUpdateTicket.bind(this),
    fail: this.failToUpdateTicket.bind(this),
  };

  private ticketInitialData(props: DatadogProps): void {
    sendLog(
      'info',
      `Dados iniciais do ticket para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private invalidJsonParser(props: DatadogError): void {
    sendLog(
      'error',
      `Erro ao parsear attachData U_JSON para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private createTicketRequest(props: DatadogProps): void {
    sendLog(
      'info',
      `Solicitação de abertura de ticket para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private createTicketSuccess(props: DatadogProps): void {
    sendLog(
      'info',
      `Criação de interação realizada com sucesso para o ContactId ${props.masterId}.`,
      props as unknown as DatadogProps,
    );
  }

  private createTicketFail(props: DatadogError): void {
    sendLog(
      'error',
      `Erro ao criar interação no dynamics para o ContactId ${props.masterId}.`,
      props as unknown as DatadogProps,
    );
  }

  private duplicatedTicketCreation(props: DatadogProps): void {
    sendLog(
      'warn',
      `Solicitação de interação duplicada para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private startRetrieveTicket(props: InteractionProps): void {
    sendLog(
      'info',
      `Recuperando Id do Ticket para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private successRetrieveTicket(props: InteractionProps): void {
    sendLog(
      'info',
      `Id Ticket recuperado com sucesso para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private excessiveAttemptsRetrieveTicketError(
    props: InteractionProps & { attempts: number },
  ): void {
    sendLog(
      'error',
      `Erro ao recuperar interação para o ContactId ${props.masterId} por excesso de tentativas (${props.attempts}).`,
      props,
    );
  }

  private failRetrieveTicket(props: InteractionProps): void {
    sendLog(
      'error',
      `Erro ao recuperar interação para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private createTicketSession(props: TemplateProps): void {
    sendLog(
      'info',
      `Abertura de sessão para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private successCreateTicketSession(props: CreateSessionSuccessProps): void {
    sendLog(
      'info',
      `Sessão criada com sucesso para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private failCreateTicket(props: TemplateProps & DatadogError): void {
    sendLog(
      'error',
      `Falha ao realizar a criação da sessão para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private uraLeaveToUra(props: DatadogProps): void {
    sendLog(
      'info',
      `Direcionado para URA para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private uraReturnToUra(props: DatadogProps): void {
    sendLog(
      'info',
      `Retornou da URA para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private uraCancel(props: DatadogProps): void {
    sendLog(
      'warn',
      `O retorno da URA foi cancelado para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private uraFail(props: DatadogProps & DatadogError): void {
    sendLog(
      'warn',
      `Falha na execução do retorno de URA para o ContactId ${props.masterId}. Erro: ${props.error}`,
      props,
    );
  }

  private beginCloseTicket(
    closeEventType: CloseTicketByEventType,
    props: CloseTicketByEventProps,
  ): void {
    sendLog(
      'info',
      `Iniciando fechamento da interação para o ContactId ${props.masterId} com evento: (${closeEventType})`,
      props,
    );
  }

  private successCloseTicket(
    closeEventType: CloseTicketByEventType,
    props: CloseTicketByEventProps,
  ): void {
    sendLog(
      'info',
      `Sessão da interação para o ContactId ${props.masterId} fechada com evento: (${closeEventType})`,
      props,
    );
  }

  private failToCloseTicket(
    closeEventType: CloseTicketByEventType,
    props: CloseTicketByEventProps & DatadogError,
  ): void {
    sendLog(
      'error',
      `Falha ao fechar sessão da interação para o ContactId ${props.masterId} com o evento: (${closeEventType})`,
      props,
    );
  }

  private failToCloseTicketByRelationship(
    closeEventType: CloseTicketByEventType,
    props: DatadogProps,
  ): void {
    sendLog(
      'error',
      `Falha ao recuperar o relacionamento da interação para o ContactId ${props.masterId} com o evento: (${closeEventType})`,
      props,
    );
  }

  private beginUpdateTicket(props: DatadogProps): void {
    sendLog(
      'info',
      `Solicitação de atualização de ticket para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private failToUpdateNotFoundTicket(props: DatadogProps): void {
    sendLog(
      'warn',
      `Interação não encontrada para atualização para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private successUpdateTicket(props: DatadogProps): void {
    sendLog(
      'info',
      `Ticket atualizado com sucesso para o ContactId ${props.masterId}.`,
      props,
    );
  }

  private failToUpdateTicket(props: DatadogError): void {
    sendLog(
      'error',
      `Erro ao atualizar ticket no dynamics para o ContactId ${props.masterId}.`,
      props,
    );
  }
}

function sendLog(type: LogTypes, message: string, props: DatadogProps) {
  if (!isValidLog(props)) return;

  const bu = location.pathname.substring(10);
  quickWebLogger.logger[type](`${bu}: ${message}`, {
    details: {
      user: getUserIfoInLocalStorage(),
      ...fixProps(props as unknown as Record<string, unknown>),
    },
  });
}

/**
 * Corrige as propriedades dos dados do Datadog.
 * Converte strings JSON em objetos e mapeia o tipo de interação.
 *
 * @param props Propriedades do Datadog a serem corrigidas
 * @returns Propriedades corrigidas e formatadas
 */
export function fixProps(props: Record<string, unknown>) {
  if (!props) return {};

  const propsRecord = deepClone(props);
  /**
   * Padrões esperados que sejam encontrados no objeto/json
   *
   * attachData.U_JSON
   * attachData.ATTACH_U_JSON
   * attachData.cb_mst_attachdata
   * attachData.itau_mst_attachdata
   */

  tryFixJson(propsRecord);

  delete propsRecord['error'];

  const foundError = props['error'] ? props['error'] : props['exception'];
  if (foundError) propsRecord['exception'] = extractError(foundError);
  if (!propsRecord['mediaType']) propsRecord['mediaType'] = 'unknown';

  return propsRecord;
}

function deepClone(props: Record<string, unknown>): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(props));
  } catch {
    return {};
  }
}

/**
 * Tenta analisar strings JSON dentro de um objeto com base nas chaves fornecidas.
 * @param collection O objeto contendo as strings JSON.
 * @param keys As chaves separadas por ponto indicando quais propriedades analisar.
 */
function tryFixJson(collection: OptionalProps) {
  for (const key of Object.keys(collection)) {
    const param = collection[key];

    if (!isAttachProperty(key)) continue;

    try {
      if (typeof param === 'string') {
        collection[key] = JsonConverterUtils.exportJsonParsed(param);
      }

      if (collection[key] && typeof collection[key] === 'object')
        tryFixJson(collection[key] as OptionalProps);
    } catch {
      /* empty */
    }
  }
}

function isAttachProperty(name: string) {
  name = name.toLowerCase();
  const knoweds = ['attachdata', 'u_json', 'attach'];
  return knoweds.find((knowed) => name.includes(knowed)) !== undefined;
}

function isValidLog(props: DatadogProps) {
  return props.masterId;
}
